import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Clock, Camera } from "lucide-react";

const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function PublicTimeline() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/timeline/${token}`)
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  if (err) return <div className="grid min-h-screen place-items-center bg-[#0b0f17] text-slate-400" data-testid="timeline-error">This project timeline is not available.</div>;
  if (!data) return <div className="grid min-h-screen place-items-center bg-[#0b0f17] text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0b0f17] text-white" data-testid="public-timeline">
      <header className="relative overflow-hidden border-b border-slate-800">
        {data.branding?.cover && <img src={data.branding.cover} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />}
        <div className="relative bg-[#0d1420]/70 px-6 py-10">
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              {data.branding?.logo
                ? <img src={data.branding.logo} alt="logo" data-testid="timeline-logo" className="h-11 w-11 rounded-lg bg-white/10 object-contain p-1" />
                : <Camera className="h-5 w-5 text-cyan-400" />}
              <span className="font-mono-tac text-xs uppercase tracking-widest text-cyan-400">{data.project.company || "Project"} · Live timeline</span>
            </div>
            <h1 className="mt-3 font-display text-3xl font-bold" data-testid="timeline-title">{data.project.title}</h1>
            {data.project.address && <p className="mt-1 flex items-center gap-1 text-sm text-slate-300"><MapPin className="h-4 w-4" /> {data.project.address}</p>}
            <p className="mt-2 font-mono-tac text-xs text-slate-400">{data.count} photos · updates in real time</p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {data.photos.map((p) => <TimelinePhoto key={p.id} p={p} />)}
          {!data.photos.length && <p className="col-span-full py-16 text-center text-slate-500">No photos shared yet.</p>}
        </div>
      </main>
    </div>
  );
}

function TimelinePhoto({ p }) {
  const [orig, setOrig] = useState(false);
  return (
    <figure className="overflow-hidden rounded-xl border border-slate-800 bg-[#0d1420]" data-testid={`timeline-photo-${p.id}`}>
      <div className="relative">
        <img src={orig && p.original ? p.original : p.data} alt={p.caption || "project photo"} className="h-52 w-full object-cover" />
        {p.original && <button data-testid={`timeline-beforeafter-${p.id}`} onClick={() => setOrig((o) => !o)}
          className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-white hover:bg-black/80">
          {orig ? "Show annotated" : "Show original"}
        </button>}
      </div>
      <figcaption className="p-3">
        {p.caption && <p className="text-sm text-slate-200">{p.caption}</p>}
        <div className="mt-1 flex flex-wrap gap-1">{p.tags.map((t) => <span key={t} className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-400">#{t}</span>)}</div>
        <div className="mt-1 flex items-center gap-1 font-mono-tac text-[10px] text-slate-500"><Clock className="h-3 w-3" /> {when(p.at)}</div>
      </figcaption>
    </figure>
  );
}
